import React, { useState, useEffect } from 'react'

import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'

import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'

import { RequiredText } from '../../RequiredText'
import { SortableAttraction } from './SortableAttraction'
import { Attraction } from './Attraction'
import { AddAttractionModal } from './AddAttractionModal'
import { DeleteModal } from '../../DeleteModal'

import './addAttraction.scss'

export default function AddAttraction({
  event,
  attractions,
  setAttractions,
  isDisabled,
  requiredFieldErrorStatus,
  toUpdate,
  setToUpdate,
  idsToRemove,
  setIdsToRemove,
}) {
  const [activeAttraction, setActiveAttraction] = useState(null)

  const [showCreate, setShowCreate] = useState(false)

  const [showDelete, setShowDelete] = useState(false)

  // local state in case attraction is discarded
  // for editing
  const [attractionLocal, setAttractionLocal] = useState()

  // local state in case attraction image is discarded - need to be seperate state because there is a lot of manipulation on it
  // for editing
  const [imageLocal, setImageLocal] = useState()

  const [coordinates, setCoordinates] = useState()

  // for editing - save attraction id for flag
  // for deleting
  const [id, setId] = useState()

  // for editing
  const [initialEditState, setInitialEditState] = useState()

  const [isBtnDisabled, setIsBtnDisabled] = useState(false)

  useEffect(() => {
    // if editing set state
    if (id && attractionLocal) {
      // Draft attractions can be saved with no artwork. Reading artwork.url
      // directly throws and unmounts the event settings page.
      const artwork = attractionLocal.artwork
      setImageLocal(artwork?.url)
      setInitialEditState(attractionLocal)
      setCoordinates(
        artwork
          ? {
              width: artwork.width,
              height: artwork.height,
            }
          : undefined
      )
    }
  }, [id])

  // keep checking properties within initial state object when it changes
  useEffect(() => {
    const artworkUnchanged =
      initialEditState?.artwork?.url === imageLocal &&
      initialEditState?.artwork?.id === attractionLocal?.artwork?.id
    setIsBtnDisabled(
      (initialEditState?.name === attractionLocal?.name && artworkUnchanged) ||
        !imageLocal ||
        !attractionLocal?.name
    )
  }, [
    initialEditState,
    imageLocal,
    attractionLocal?.name,
    attractionLocal?.artwork?.id,
  ])

  // check if attraction should be updated in db
  const shouldUpdate = (attractions) => {
    const shouldAddAttraction = (attraction) => {
      const original = event?.attractions?.find((a) => a.id === attraction.id)
      if (!original) return

      const hasHttpsArtwork = attraction.artwork?.url?.startsWith('https://')
      const artworkUnchanged =
        hasHttpsArtwork &&
        ((attraction.artwork?.id &&
          original.artwork?.id === attraction.artwork?.id) ||
          (!attraction.artwork?.id &&
            original.artwork?.url === attraction.artwork?.url))

      // Name/order/primary-only change with same artwork
      if (artworkUnchanged) {
        setToUpdate((prevState) => [
          ...prevState.filter((attr) => attr.id !== attraction.id),
          attraction,
        ])
        return
      }

      // Artwork changed (new upload or different reused media) — remove old, recreate on save
      if (!idsToRemove?.some((removeId) => attraction.id === removeId)) {
        setIdsToRemove([...idsToRemove, attraction.id])
      }
    }

    // If `attractions` is an array, iterate through each item
    if (Array.isArray(attractions)) {
      // filter out new attractions - keep only existing attractions
      attractions = attractions?.filter((attr) =>
        event.attractions.find((a) => a.id === attr.id)
      )
      attractions.forEach(shouldAddAttraction)
    } else {
      // If `attractions` is a single item, process it directly
      shouldAddAttraction(attractions)
    }
  }

  const handleShowCreate = (_, attraction) => {
    setShowCreate(true)
    if (attraction) {
      setId(attraction?.id)
      setAttractionLocal(attraction)
      setImageLocal(attraction.artwork?.url)
    }
  }

  const handleCloseCreate = () => {
    setAttractionLocal()
    setImageLocal()
    setCoordinates()
    setId()
    setShowCreate(false)
    setInitialEditState()
  }

  const resolveArtworkPayload = () => {
    const isReusedArtwork = String(imageLocal || '').startsWith('https://')
    return {
      ...(isReusedArtwork && attractionLocal?.artwork?.id
        ? { id: attractionLocal.artwork.id }
        : {}),
      url: imageLocal,
      width: coordinates?.width || attractionLocal?.artwork?.width || 1080,
      height: coordinates?.height || attractionLocal?.artwork?.height || 1080,
    }
  }

  // create new or update attraction
  const handleSubmit = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!attractionLocal?.name || !imageLocal) return

    // update attraction
    if (id) {
      const updatedAttractions = attractions.map((attraction) => {
        if (attraction.id === id) {
          const updatedAttraction = {
            ...attraction,
            name: attractionLocal.name,
            order: attractionLocal.order,
            artwork: {
              ...attraction.artwork,
              ...resolveArtworkPayload(),
            },
          }
          shouldUpdate(updatedAttraction)
          return updatedAttraction
        }
        return attraction
      })

      setAttractions(updatedAttractions)
    } else {
      // create new attraction — mark isNew so save creates once, not again on later saves
      setAttractions([
        ...attractions,
        {
          id: `new-${Date.now()}`,
          isNew: true,
          name: attractionLocal.name,
          primary: attractions?.some((attraction) => attraction?.primary)
            ? false
            : true,
          order: attractions?.some((attraction) => attraction?.primary)
            ? attractions.length + 1
            : 1,
          artwork: resolveArtworkPayload(),
        },
      ])
    }
    handleCloseCreate()
  }

  const handleShowDelete = (id) => {
    setShowDelete(true)
    setId(id)
  }

  const handleCloseDelete = () => {
    setShowDelete(false)
    setId()
  }

  // remove attraction
  const handleRemove = () => {
    // if attraction to delete is found in event attractions, add its id to array to remove when saving
    if (event?.attractions?.some((attraction) => attraction.id === id)) {
      setIdsToRemove([...idsToRemove, id])
    }

    let updatedAttractions = attractions?.filter(
      (attraction) => attraction.id !== id
    )

    // Find the attraction to delete and check its order and primary status
    const deletedAttraction = attractions.find(
      (attraction) => attraction.id === id
    )
    const deletedOrder = deletedAttraction.order
    const wasPrimary = deletedAttraction.primary
    let updatedAttraction
    let attractionsToUpdate = []
    let attractionsAfterDeleted = []

    // deleted attraction was primary or position was at start or middle of array
    if (deletedOrder < attractions?.length || wasPrimary) {
      attractionsAfterDeleted = updatedAttractions
        .filter((attraction) => attraction.order > deletedOrder)
        .map((attraction, index) => {
          // Decrease order by 1 for attractions after the deleted one
          updatedAttraction = { ...attraction, order: attraction.order - 1 }

          // If the deleted attraction was primary, set the new first item to primary
          if (wasPrimary && index === 0) {
            updatedAttraction = { ...updatedAttraction, primary: true }
          }
          attractionsToUpdate = [...attractionsToUpdate, updatedAttraction]

          return updatedAttraction
        })
      shouldUpdate(attractionsToUpdate)
    }

    if (attractionsAfterDeleted?.length > 0) {
      // Combine updated attractions, keeping the original order for those before the deleted item
      updatedAttractions = [
        ...updatedAttractions.filter(
          (attraction) => attraction.order < deletedOrder
        ),
        ...attractionsAfterDeleted,
      ]
    }

    setAttractions(updatedAttractions)
    handleCloseDelete()
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = (e) => {
    setActiveAttraction(
      attractions.find((attraction) => attraction.id == e.active.id)
    )
  }

  const handleDragEnd = (e) => {
    const { active, over } = e
    // No-op if drop target is missing or unchanged
    if (!over || active.id === over.id) {
      setActiveAttraction(null)
      return
    }

    // Use the rendered order to determine new positions
    const activeId = active.id
    const overId = over.id
    const orderedAttractions = [...attractions]
    const oldIndex = orderedAttractions.findIndex(
      (attraction) => attraction.id === activeId
    )
    const newIndex = orderedAttractions.findIndex(
      (attraction) => attraction.id === overId
    )
    if (oldIndex === -1 || newIndex === -1) {
      setActiveAttraction(null)
      return
    }

    // Move the attraction in the array
    let updatedAttractions = arrayMove(orderedAttractions, oldIndex, newIndex)

    // Keep "primary" tied to the first position after reordering
    updatedAttractions = updatedAttractions.map((attraction, idx) => ({
      ...attraction,
      primary: idx === 0,
      order: idx + 1,
    }))

    // Find which attractions changed (order or primary) for updates
    const attractionsToUpdate = updatedAttractions.filter((attraction) => {
      const original = orderedAttractions.find(
        (item) => item.id === attraction.id
      )
      return (
        original &&
        (original.order !== attraction.order ||
          original.primary !== attraction.primary)
      )
    })
    shouldUpdate(attractionsToUpdate)
    setAttractions(updatedAttractions)

    setActiveAttraction(null)
  }

  return (
    <>
      <div className="card-body-heading card-body-heading--flex">
        <Card.Title as="h5">Add Attraction</Card.Title>
        {requiredFieldErrorStatus?.attractions && <RequiredText />}
      </div>
      <Stack
        direction="horizontal"
        as="ul"
        gap={4}
        className="flex-wrap align-items-stretch"
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={attractions}>
            {attractions.map((attraction, index, arr) => (
              <SortableAttraction
                key={index}
                attraction={attraction}
                idx={index}
                isDraggingDisabled={arr.length < 2}
                isDisabled={isDisabled}
                handleShowCreate={handleShowCreate}
                handleShowDelete={handleShowDelete}
              />
            ))}
          </SortableContext>
          <DragOverlay style={{ transformOrigin: '0 0 ' }} wrapperElement="ul">
            {activeAttraction ? (
              <Attraction attraction={activeAttraction} />
            ) : null}
          </DragOverlay>
        </DndContext>
        {attractions?.length < 9 && (
          <Button
            variant="outline-light"
            className={`btn--square btn-card btn-card--add ${requiredFieldErrorStatus?.attractions ? 'error' : ''}`}
            onClick={handleShowCreate}
            disabled={isDisabled}
          >
            <div className="btn-container">
              <span>Add Attraction</span>
            </div>
          </Button>
        )}
      </Stack>

      <AddAttractionModal
        show={showCreate}
        handleClose={handleCloseCreate}
        id={id}
        attraction={attractionLocal}
        setAttraction={setAttractionLocal}
        image={imageLocal}
        setImage={setImageLocal}
        coordinates={coordinates}
        setCoordinates={setCoordinates}
        handleSubmit={handleSubmit}
        isDisabled={isBtnDisabled}
      />

      <DeleteModal
        entity="attraction"
        show={showDelete}
        handleClose={handleCloseDelete}
        id={id}
        handleDelete={handleRemove}
      />
    </>
  )
}
